import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Menu, Heart, Phone, X, ShieldCheck, GitCompare } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { useApp } from "../context/AppContext";
import api from "../lib/api";

const navLinks = [
  { to: "/properties", label: "Properties" },
  { to: "/plots", label: "Plots" },
  { to: "/builder-floors", label: "Builder Floors" },
  { to: "/flats", label: "Flats" },
  { to: "/commercial", label: "Commercial" },
  { to: "/locations", label: "Sectors" },
  { to: "/blog", label: "Guides" },
];

export default function Header() {
  const { saved, compare, settings } = useApp();
  const [open, setOpen] = useState(false);
  const { data: menuPages } = useQuery({ queryKey: ["menu-pages"], queryFn: async () => (await api.get("/pages")).data });
  const extraLinks = (menuPages?.items || []).map((p) => ({ to: `/p/${p.slug}`, label: p.menu_label || p.title }));
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${scrolled ? "bg-white/90 backdrop-blur-md border-b border-slate-100 shadow-sm" : "bg-white/70 backdrop-blur-sm"}`} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="Faridabad Properties" className="h-9 md:h-11 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-indigo-800 transition-colors rounded-lg hover:bg-indigo-50">
                {l.label}
              </Link>
            ))}
            {extraLinks.map((l) => (
              <Link key={l.to} to={l.to} className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-indigo-800 transition-colors rounded-lg hover:bg-indigo-50">{l.label}</Link>
            ))}
          </nav>

          <div className="flex items-center gap-1 md:gap-2">
            <Link to="/compare" className="relative p-2 text-slate-600 hover:text-indigo-800" data-testid="header-compare" title="Compare">
              <GitCompare className="w-5 h-5" />
              {compare.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-indigo-700 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{compare.length}</span>}
            </Link>
            <Link to="/saved-properties" className="relative p-2 text-slate-600 hover:text-rose-600" data-testid="header-saved" title="Saved">
              <Heart className="w-5 h-5" />
              {saved.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{saved.length}</span>}
            </Link>
            <a href={`tel:${settings.phone || ""}`} data-testid="header-call-expert" className="hidden md:inline-flex">
              <Button className="bg-indigo-900 hover:bg-indigo-800 rounded-xl" size="sm">
                <Phone className="w-4 h-4 mr-2" /> Talk to Expert
              </Button>
            </a>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 text-slate-700" data-testid="mobile-menu-trigger"><Menu className="w-6 h-6" /></button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px]">
                <div className="flex flex-col gap-1 mt-8">
                  {navLinks.map((l) => (
                    <Link key={l.to} to={l.to} className="px-3 py-3 text-base font-medium text-slate-700 border-b border-slate-100" data-testid={`mnav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>{l.label}</Link>
                  ))}
                  {extraLinks.map((l) => (
                    <Link key={l.to} to={l.to} className="px-3 py-3 text-base font-medium text-slate-700 border-b border-slate-100">{l.label}</Link>
                  ))}
                  <Link to="/book-site-visit" className="px-3 py-3 text-base font-medium text-slate-700 border-b border-slate-100">Book Site Visit</Link>
                  <Link to="/about" className="px-3 py-3 text-base font-medium text-slate-700 border-b border-slate-100">About</Link>
                  <Link to="/contact" className="px-3 py-3 text-base font-medium text-slate-700 border-b border-slate-100">Contact</Link>
                  <a href={`tel:${settings.phone || ""}`} className="mt-4">
                    <Button className="w-full bg-indigo-900 hover:bg-indigo-800"><Phone className="w-4 h-4 mr-2" /> Talk to Expert</Button>
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
